var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import cn from 'classnames';
import DOMPurification from 'dompurify';
import { Check } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue, } from '@/components/ui/select';
import { languageByCountryCode } from '@/config/constants';
import { AnalyticsEvent, useAnalytics } from '@/hooks/useAnalytics';
import { useHandleLinksManually } from '@/hooks/useHandleLinksManually';
import { useSocket } from '@/hooks/useSocket';
import { visitorInformationSelector } from '@/state/config';
import { emailState, sessionIdSelector, userSelector } from '@/state/session';
import { emailRegExp, validatePhoneNumber, getLocalDateTimeString, setNestedValue, mergeDeep, } from '@/util/utils';
const getValidationRules = (fieldName, required, t, setValue, variableType) => {
    const baseRules = {
        required: required ? t('fields.required') : undefined,
    };
    switch (fieldName) {
        case 'display_name':
            return Object.assign(Object.assign({}, baseRules), { maxLength: {
                    value: 32,
                    message: t('fields.less_than', {
                        0: 32,
                    }),
                } });
        case 'email':
            return Object.assign(Object.assign({}, baseRules), { pattern: {
                    value: emailRegExp,
                    message: t('fields.invalid'),
                } });
        case 'phone':
            return Object.assign(Object.assign({}, baseRules), { validate: (value) => {
                    if (!value || typeof value !== 'string') {
                        return true;
                    }
                    const result = validatePhoneNumber(value);
                    if (!result.valid) {
                        return t('fields.invalid');
                    }
                    // Format the phone number and update the form value
                    // We use shouldValidate: false to prevent validation loop
                    if (setValue) {
                        setValue('phone', result.formatted, { shouldValidate: false });
                    }
                    return true;
                }, maxLength: {
                    value: 24,
                    message: t('fields.less_than', {
                        0: 24,
                    }),
                } });
        case 'address':
            return Object.assign(Object.assign({}, baseRules), { minLength: {
                    value: 5,
                    message: t('fields.more_than', {
                        0: 5,
                    }),
                }, maxLength: {
                    value: 128,
                    message: t('fields.less_than', {
                        0: 128,
                    }),
                } });
        case 'language':
            return baseRules;
        case 'disclaimer_accepted':
            return {
                required: t('disclaimer.required'),
            };
        default:
            // For custom fields, return basic validation
            if (variableType === 'boolean' && required) {
                // For boolean fields, we need custom validation since React Hook Form's required
                // rule doesn't work correctly with boolean values (false is a valid value)
                return {
                    validate: (value) => {
                        if (value === undefined) {
                            return t('fields.required');
                        }
                        return true;
                    },
                    maxLength: {
                        value: 128,
                        message: t('fields.less_than', {
                            0: 128,
                        }),
                    },
                };
            }
            return Object.assign(Object.assign({}, baseRules), { maxLength: {
                    value: 128,
                    message: t('fields.less_than', {
                        0: 128,
                    }),
                } });
    }
};
export const WelcomeFields = () => {
    const sanitizer = DOMPurification.sanitize;
    const [isLoading, setIsLoading] = useState(false);
    const { t, i18n } = useTranslation();
    const { updateContext } = useSocket();
    const sessionId = useRecoilValue(sessionIdSelector);
    const user = useRecoilValue(userSelector);
    const { headline, disclaimer, fields } = useRecoilValue(visitorInformationSelector);
    const languageField = fields === null || fields === void 0 ? void 0 : fields.find(({ field }) => field === 'language');
    const { trackEvent } = useAnalytics();
    const setEmail = useSetRecoilState(emailState);
    const { handleLinkClick } = useHandleLinksManually();
    const defaultValues = Object.assign(Object.assign({}, fields === null || fields === void 0 ? void 0 : fields.reduce((acc, { field, variable_type, variable }) => {
        var _a;
        if (field === 'custom') {
            const variableName = variable || 'custom';
            let value;
            if (variable_type === 'boolean') {
                value = false;
            }
            else if (variable_type === 'date_time') {
                value = getLocalDateTimeString(new Date());
            }
            if (variableName.includes('.')) {
                // Build nested default for dotted paths
                const nested = setNestedValue(variableName, value);
                return mergeDeep(acc, nested);
            }
            return Object.assign(Object.assign({}, acc), { [variableName]: value });
        }
        return Object.assign(Object.assign({}, acc), { [field]: (user === null || user === void 0 ? void 0 : user[field]) &&
                user[field] !== '***' &&
                !((_a = user[field]) === null || _a === void 0 ? void 0 : _a.startsWith('Visitor '))
                ? user[field]
                : '' });
    }, {})), { disclaimer_accepted: false });
    const form = useForm({
        defaultValues,
    });
    const isDisclaimerEnabled = !!(disclaimer === null || disclaimer === void 0 ? void 0 : disclaimer.enabled) && !!(disclaimer === null || disclaimer === void 0 ? void 0 : disclaimer.html);
    const hasFields = fields && fields.length > 0;
    // Reset the loading if the sessionId changes
    useEffect(() => {
        queueMicrotask(() => setIsLoading(false));
    }, [sessionId]);
    const onSubmit = useCallback((formData) => {
        trackEvent(AnalyticsEvent.WelcomeFormSubmitted);
        setIsLoading(true);
        const { display_name, email, phone, address, language, disclaimer_accepted } = formData, customFields = __rest(formData, ["display_name", "email", "phone", "address", "language", "disclaimer_accepted"]);
        const user = {
            display_name,
            email,
            phone,
            address,
            language,
        };
        // Cleanup any undefined values from the user object
        Object.keys(user).forEach((key) => {
            if (user[key] === undefined || user[key] === '' || user[key] === null) {
                delete user[key];
            }
        });
        if (language) {
            i18n.changeLanguage(language);
        }
        if (email) {
            setEmail(email);
        }
        // Update the context with the processed data
        updateContext(Object.assign({ user, global: Object.assign({ visitor_form_completed: true }, (isDisclaimerEnabled ? { disclaimer_accepted } : {})) }, customFields));
    }, [i18n, isDisclaimerEnabled, setEmail, trackEvent, updateContext]);
    const handleTermsLinkClick = useCallback((e) => {
        trackEvent(AnalyticsEvent.TermsAndConditionsLinkClicked);
        handleLinkClick(e);
    }, [handleLinkClick, trackEvent]);
    return (_jsx("div", { className: "relative flex h-full flex-1 flex-col justify-between overflow-hidden", children: _jsxs("div", { className: cn('mt-[var(--moveo-new-header-height)] flex h-full max-h-[calc(100%-68px)] flex-col overflow-auto px-6', {
                'justify-between': isDisclaimerEnabled && !hasFields,
            }), children: [headline && (_jsx("p", { className: "mb-2 mt-3 text-lg font-semibold leading-7 text-foreground", children: headline })), _jsx(Form, Object.assign({}, form, { children: _jsxs("form", { autoComplete: "off", className: "h-full[calc(100%-68px)] mx-auto mt-4 flex w-full max-w-md flex-col gap-4", onSubmit: form.handleSubmit(onSubmit), children: [fields === null || fields === void 0 ? void 0 : fields.map(({ field, required, variable, variable_type, field_label }) => {
                                if (field === 'language') {
                                    return (_jsx(FormField, { control: form.control, name: field, rules: getValidationRules(field, required, t), render: ({ field: controllerField }) => {
                                            var _a;
                                            return (_jsxs(FormItem, { children: [_jsx(FormLabel, { children: t(`fields.${field}`) }), _jsx(FormControl, { children: _jsxs(Select, { name: "language", value: controllerField.value, onValueChange: controllerField.onChange, children: [_jsx(SelectTrigger, { className: "w-full", children: _jsx(SelectValue, { placeholder: t('fields.placeholder.language') }) }), _jsx(SelectContent, { children: _jsxs(SelectGroup, { children: [_jsx(SelectLabel, { children: t(`fields.${field}`) }), (_a = languageField === null || languageField === void 0 ? void 0 : languageField.languages) === null || _a === void 0 ? void 0 : _a.map((option) => (_jsx(SelectItem, { value: option, children: languageByCountryCode[option] }, option)))] }) })] }) }), _jsx(FormMessage, {})] }));
                                        } }, field));
                                }
                                if (field === 'custom') {
                                    // For custom fields, use the variable name as the form field key
                                    // This can be a JSON path like 'custom.field' or 'settings.theme'
                                    // We keep it flat for form handling and convert to nested structure on submission
                                    const variableName = variable || 'custom';
                                    return (_jsx(FormField, { control: form.control, name: variableName, rules: getValidationRules(variableName, required, t, form.setValue, variable_type), render: ({ field: controllerField }) => {
                                            switch (variable_type) {
                                                case 'boolean':
                                                    return (_jsxs(FormItem, { children: [_jsx(FormControl, { children: _jsxs("div", { className: "flex items-center space-x-2", children: [_jsx(Checkbox, { id: variableName, className: "bg-background", checked: controllerField.value, onCheckedChange: controllerField.onChange }), _jsx(FormLabel, { className: "mt-0", children: field_label || t('fields.custom_field') })] }) }), _jsx(FormMessage, {})] }));
                                                case 'date_time':
                                                    return (_jsxs(FormItem, { children: [_jsx(FormLabel, { children: field_label || t('fields.custom_field') }), _jsx(FormControl, { children: _jsx(Input, Object.assign({}, controllerField, { value: controllerField.value, type: "datetime-local", className: "w-full" })) }), _jsx(FormMessage, {})] }));
                                                default:
                                                    return (_jsxs(FormItem, { children: [_jsx(FormLabel, { children: field_label || t('fields.custom_field') }), _jsx(FormControl, { children: _jsx(Input, Object.assign({}, controllerField, { value: controllerField.value, className: "w-full", type: variable_type === 'number'
                                                                        ? 'number'
                                                                        : 'text' })) }), _jsx(FormMessage, {})] }));
                                            }
                                        } }, variableName));
                                }
                                return (_jsx(FormField, { control: form.control, name: field, rules: getValidationRules(field, required, t, form.setValue), render: ({ field: controllerField }) => {
                                        return (_jsxs(FormItem, { children: [_jsx(FormLabel, { children: t(`fields.${field}`) }), _jsx(FormControl, { children: _jsx(Input, Object.assign({}, controllerField, { className: "w-full" })) }), _jsx(FormMessage, {})] }));
                                    } }, field));
                            }), isDisclaimerEnabled && (_jsx("div", { className: "on mb-20 mt-4", children: _jsx(FormField, { control: form.control, name: "disclaimer_accepted", rules: getValidationRules('disclaimer_accepted', true, t), render: ({ field: controllerField, fieldState }) => {
                                        return (_jsx(FormControl, { children: _jsxs(_Fragment, { children: [_jsxs("div", { role: "checkbox", "aria-checked": controllerField.value, tabIndex: 0, className: "bg-bg-muted mt-auto max-w-md cursor-pointer rounded-[var(--moveo-new-border-radius-base)] p-4", onClick: (e) => {
                                                            // Don't toggle checkbox when clicking on links
                                                            if (e.target.tagName === 'A') {
                                                                handleTermsLinkClick(e);
                                                                return;
                                                            }
                                                            controllerField.onChange(!controllerField.value);
                                                        }, onKeyDown: (e) => {
                                                            // Toggle checkbox on Space/Enter, but not when on links
                                                            if ((e.key === ' ' || e.key === 'Enter') &&
                                                                e.target.tagName !== 'A') {
                                                                e.preventDefault();
                                                                controllerField.onChange(!controllerField.value);
                                                            }
                                                        }, children: [_jsx("p", { className: cn('mb-2 text-sm font-semibold leading-5', fieldState.error
                                                                    ? 'text-warning'
                                                                    : 'text-foreground'), children: t('disclaimer.title') }), _jsxs("div", { className: "flex gap-2", children: [_jsx("div", { className: cn('mt-1 flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border border-input bg-background', {
                                                                            'border-primary bg-primary': controllerField.value,
                                                                        }), children: controllerField.value && (_jsx(Check, { className: "h-3 w-3 text-primary-foreground" })) }), _jsx("div", { className: `${"disclaimer-container" /* TestClassName.DisclaimerContainer */} text-foreground [&_a:hover]:underline [&_a]:cursor-pointer [&_a]:text-accent`, dangerouslySetInnerHTML: {
                                                                            __html: sanitizer(disclaimer === null || disclaimer === void 0 ? void 0 : disclaimer.html, {
                                                                                ADD_ATTR: ['target'],
                                                                            }),
                                                                        } })] })] }), _jsx(FormMessage, { className: "mt-2" })] }) }));
                                    } }) })), _jsx("div", { className: "bg-chat-background absolute bottom-0 left-0 right-0 z-10 mx-3 flex h-14 w-[calc(100%-24px)] justify-center", children: _jsx(Button, { type: "submit", isLoading: isLoading, className: "w-full max-w-md", children: t('buttons.continue') }) })] }) }))] }) }));
};
